import { AccountInfo, InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';

// Two ways in, one session model:
//   Microsoft 365  -> MSAL (any organization); the API validates the Entra access token.
//   Google Workspace -> authorization code + PKCE; the API exchanges the code with Google (client secret stays
//                       server-side) and returns a BeefedUp session token.
// Which organization the user belongs to decides which apps and which data they see - enforced by the API.

export const clientId: string = import.meta.env.VITE_CLIENT_ID as string;
export const apiBaseUrl: string = (import.meta.env.VITE_API_BASE_URL as string).replace(/\/+$/, '');
export const appVersion: string = (import.meta.env.VITE_APP_VERSION as string) || '2.0.0';
const scopes: string[] = [`api://${clientId}/access_as_user`];
const GOOGLE_KEY: string = 'bu.google.session';
const GOOGLE_FLOW_KEY: string = 'bu.google.flow';
const GOOGLE_REDIRECT_PATH: string = '/auth/google';
const CLIENT_KEY: string = 'bu.client';

// The organization this browser is working in, for people who belong to more than one (the API checks it).
export const getActiveClient = (): string => { try { return localStorage.getItem(CLIENT_KEY) || ''; } catch { return ''; } };
export const setActiveClient = (tenantId: string): void => { try { localStorage.setItem(CLIENT_KEY, tenantId); } catch { /* private mode */ } };

// Platform owner: viewing the apps as one of a client's people (read-only; the API refuses every change).
// Kept per browser tab, so closing the tab ends it.
const VIEW_AS_KEY: string = 'bu.viewAs';
export interface IViewAs { tenantId: string; email: string; name: string; clientName: string; }
export const getViewAs = (): IViewAs | null => { try { return JSON.parse(sessionStorage.getItem(VIEW_AS_KEY) || 'null'); } catch { return null; } };
export const viewAsHeaders = (): { [key: string]: string } => { const v = getViewAs(); return v !== null ? { 'X-View-As': `${v.tenantId}:${v.email}` } : {}; };
export const startViewAs = (v: IViewAs): void => { try { sessionStorage.setItem(VIEW_AS_KEY, JSON.stringify(v)); } catch { /* private mode */ } window.location.assign('/apps'); };
export const stopViewAs = (): void => {
  const v = getViewAs();
  try { sessionStorage.removeItem(VIEW_AS_KEY); } catch { /* private mode */ }
  window.location.assign(v !== null ? `/admin/team?tenantId=${v.tenantId}` : '/apps');
};

export type Provider = 'microsoft' | 'google';

export const msal: PublicClientApplication = new PublicClientApplication({
  auth: {
    clientId,
    authority: 'https://login.microsoftonline.com/organizations',
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin
  },
  cache: { cacheLocation: 'localStorage' }
});

let account: AccountInfo | undefined;
let provider: Provider | null = null;

interface IGoogleSession { token: string; expiresAt: number; email: string; }

const readGoogle = (): IGoogleSession | null => {
  try {
    const s: IGoogleSession = JSON.parse(localStorage.getItem(GOOGLE_KEY) || 'null');
    return s !== null && s.expiresAt * 1000 > Date.now() + 60000 ? s : null;
  } catch { return null; }
};

const safeReturn = (p: string | null | undefined): string => (p && p.charAt(0) === '/' && p.charAt(1) !== '/' ? p : '/apps');

// ---------------------------------------------------------------------------
// Startup: finish any sign-in redirect in flight, then report who (if anyone) is signed in.
// ---------------------------------------------------------------------------

export const initAuth = async (): Promise<{ provider: Provider | null; error?: string }> => {
  await msal.initialize();
  const redirect = await msal.handleRedirectPromise();
  if (redirect !== null) {
    account = redirect.account;
    if (redirect.state) { window.history.replaceState(null, '', safeReturn(decodeURIComponent(redirect.state))); }
  }

  if (window.location.pathname === GOOGLE_REDIRECT_PATH) {
    try {
      await completeGoogle();
    } catch (e) {
      window.history.replaceState(null, '', '/signin');
      return { provider: null, error: (e as Error).message };
    }
  }

  account = account ?? msal.getAllAccounts()[0];
  if (account !== undefined) { msal.setActiveAccount(account); provider = 'microsoft'; }
  else if (readGoogle() !== null) { provider = 'google'; }
  return { provider };
};

export const currentProvider = (): Provider | null => provider;

// ---------------------------------------------------------------------------
// Microsoft
// ---------------------------------------------------------------------------

export const signInMicrosoft = (returnTo?: string): void => {
  void msal.loginRedirect({ scopes, state: encodeURIComponent(safeReturn(returnTo)), prompt: 'select_account' });
};

// ---------------------------------------------------------------------------
// Google (authorization code + PKCE)
// ---------------------------------------------------------------------------

const b64url = (bytes: ArrayBuffer | Uint8Array): string =>
  btoa(String.fromCharCode(...new Uint8Array(bytes))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

export const signInGoogle = async (googleClientId: string, returnTo?: string, loginHint?: string): Promise<void> => {
  const verifier: string = b64url(crypto.getRandomValues(new Uint8Array(48)));
  const challenge: string = b64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
  const state: string = b64url(crypto.getRandomValues(new Uint8Array(16)));
  sessionStorage.setItem(GOOGLE_FLOW_KEY, JSON.stringify({ verifier, state, returnTo: safeReturn(returnTo) }));
  const q: URLSearchParams = new URLSearchParams({
    client_id: googleClientId,
    redirect_uri: window.location.origin + GOOGLE_REDIRECT_PATH,
    response_type: 'code',
    scope: 'openid email profile',
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
    hd: '*',                       // offer Workspace accounts only
    prompt: loginHint ? 'none' : 'select_account'
  });
  if (loginHint) { q.set('login_hint', loginHint); }
  window.location.assign(`https://accounts.google.com/o/oauth2/v2/auth?${q.toString()}`);
};

const completeGoogle = async (): Promise<void> => {
  const params: URLSearchParams = new URLSearchParams(window.location.search);
  const flow = JSON.parse(sessionStorage.getItem(GOOGLE_FLOW_KEY) || 'null') as { verifier: string; state: string; returnTo: string } | null;
  sessionStorage.removeItem(GOOGLE_FLOW_KEY);
  if (params.get('error')) { throw new Error(params.get('error') === 'access_denied' ? 'Google sign-in was cancelled.' : `Google sign-in failed (${params.get('error')}).`); }
  if (flow === null || params.get('state') !== flow.state || !params.get('code')) { throw new Error('Google sign-in could not be verified. Please try again.'); }
  const resp: Response = await fetch(`${apiBaseUrl}/api/portal/google/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code: params.get('code'), codeVerifier: flow.verifier, redirectUri: window.location.origin + GOOGLE_REDIRECT_PATH })
  });
  const data = await resp.json().catch(() => ({})) as { token?: string; expiresAt?: number; error?: string };
  if (!resp.ok || !data.token) { throw new Error(data.error || 'Google sign-in failed.'); }
  const email: string = JSON.parse(atob(data.token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).email || '';
  localStorage.setItem(GOOGLE_KEY, JSON.stringify({ token: data.token, expiresAt: data.expiresAt, email }));
  window.history.replaceState(null, '', flow.returnTo);
};

// ---------------------------------------------------------------------------
// Tokens + sign-out
// ---------------------------------------------------------------------------

let googleClientIdCache: string = '';
export const setGoogleClientId = (id: string): void => { googleClientIdCache = id; };

export const getToken = async (): Promise<string> => {
  if (provider === 'google') {
    const s: IGoogleSession | null = readGoogle();
    if (s !== null) { return s.token; }
    // Session over: quietly go back through Google (no prompt if they're still signed in there).
    const last = JSON.parse(localStorage.getItem(GOOGLE_KEY) || 'null') as IGoogleSession | null;
    if (googleClientIdCache !== '') { await signInGoogle(googleClientIdCache, window.location.pathname, last?.email); }
    throw new Error('Session expired');
  }
  if (account === undefined) { throw new Error('Not signed in'); }
  try {
    return (await msal.acquireTokenSilent({ scopes, account })).accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) { await msal.acquireTokenRedirect({ scopes, account, state: encodeURIComponent(window.location.pathname) }); }
    throw e;
  }
};

export const signOut = (): void => {
  try { sessionStorage.removeItem(VIEW_AS_KEY); } catch { /* private mode */ }
  if (provider === 'google') {
    localStorage.removeItem(GOOGLE_KEY);
    window.location.assign('/');
    return;
  }
  void msal.logoutRedirect({ account });
};

// ---------------------------------------------------------------------------
// Platform API
// ---------------------------------------------------------------------------

export const publicConfig = async (): Promise<{ google: { clientId: string } | null }> => {
  try {
    const r: Response = await fetch(`${apiBaseUrl}/api/portal/config`);
    const c = await r.json() as { google: { clientId: string } | null };
    if (c.google) { setGoogleClientId(c.google.clientId); }
    return c;
  } catch { return { google: null }; }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const portalCall = async <T = any>(method: string, path: string, body?: unknown): Promise<T> => {
  const resp: Response = await fetch(`${apiBaseUrl}/api/portal/${path}`, {
    method,
    headers: { Authorization: `Bearer ${await getToken()}`, ...(getActiveClient() ? { 'X-Client': getActiveClient() } : {}), ...viewAsHeaders(), ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined
  });
  const text: string = await resp.text();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data: any = text !== '' ? JSON.parse(text) : null;
  if (!resp.ok) { throw new Error(data !== null && data.error ? data.error : `HTTP ${resp.status}`); }
  return data as T;
};

export interface IAppInfo { key: string; name: string; description: string; path: string; url?: string; }
export interface IMembership { tenantId: string; name: string; roleName: string; }
export interface IPortalMe {
  name: string;
  email: string;
  provider: 'entra' | 'google';
  tenantId: string;                       // the account's own organization
  isOwner: boolean;                       // platform owner (BeefedUp IT)
  isAdmin: boolean;                       // can manage people in the current organization
  client: { tenantId: string; name: string } | null;   // the organization being worked in
  role: { key: string; name: string } | null;
  permissions: string[];
  memberships: IMembership[];
  viewedBy: string | null;                // set while the platform owner views as this person
  apps: IAppInfo[];
}

export const canManagePeople = (me: IPortalMe): boolean =>
  me.isOwner || ['people.invite', 'people.assign', 'people.roles'].some((p: string): boolean => me.permissions.indexOf(p) !== -1);

/** Public: what an invitation link is for (shown before sign-in). */
export const peekInvite = async (c: string, t: string): Promise<{ clientName: string; email: string; invitedBy: string; roleName: string; expired: boolean }> => {
  const r: Response = await fetch(`${apiBaseUrl}/api/portal/invites/peek?c=${encodeURIComponent(c)}&t=${encodeURIComponent(t)}`);
  const data = await r.json().catch(() => ({})) as { error?: string };
  if (!r.ok) { throw new Error(data.error || `HTTP ${r.status}`); }
  return data as { clientName: string; email: string; invitedBy: string; roleName: string; expired: boolean };
};
